<template>
    <div class="detail-charts">
        <div class="chart-panel">
            <transition name="fade">
                <div v-if="loading" class="loading-wrapper">
                    <BaseSpinner text="info.loading_data" />
                </div>
            </transition>
            <div class="chart-panel__header" :id="`test-detail-chart-events`">
                <div>{{ $t(`metrics.events`) }}</div>
                <div class="chart-panel__info__period" @click="toggleChartPeriodSelect2" v-click-outside="hideChartPeriodSelect2" v-responsive.sm.xs.md>
                    <div style="display: flex;">
                        <span>{{ selectedPeriod.name }}</span>
                        <BaseIcon icon="chevron-down" />
                    </div>
                    <transition name="fadeUp">
                        <BaseSelectList
                                v-if="showChartPeriodSelect2"
                                :items="chartPeriodOptions"
                                :active="selectedPeriod"
                                @onSelect="setChartPeriod"
                                style="z-index: 13;"
                        />
                    </transition>
                </div>
                <div v-if="false" class="chart-panel__header__settings">
                    <BaseIcon icon="settings" @click="toggleChartSettings" />
                    <transition name="fadeUp">
                        <div v-if="showChartSettings" class="graph-settings"></div>
                    </transition>
                </div>
            </div>
            <div class="chart-panel__info" v-responsive.lg.xl.md>
                <div class="chart-panel__info__actual" v-if="this.hasValues">
                    <span class="description">{{ $t('eventChart.latestEvent') }}</span>
                    <span class="value">{{ lastEventMessage }}</span>
                </div>
                <div class="chart-panel__info__period test-button-time-range" @click="toggleChartPeriodSelect" v-click-outside="hideChartPeriodSelect" v-responsive.lg.xl>
                    <div style="display: flex;">
                        {{ $t('detail.select_data') }}:
                        <span>{{ computedPeriodOption.name }}</span>
                        <BaseIcon icon="chevron-down" />
                    </div>
                    <transition name="fadeUp">
                        <BaseSelectList
                                v-if="showChartPeriodSelect"
                                :items="chartPeriodOptions"
                                :active="computedPeriodOption"
                                @onSelect="setChartPeriod"
                                style="z-index: 13;"
                        />
                    </transition>
                </div>
                <div v-if="showCustomDateRange" class="date-range-picker">
                    <date-range-picker
                            ref="picker"
                            :opens="'top'"
                            :showControls="true"
                            :locale-data="{ firstDay: 1, format: 'dd-mm-yyyy' }"
                            :singleDatePicker="false"
                            @update="setChartcustomPeriod"
                            @click="checkOpen"
                            :theme="rangePickerTheme"
                            :submitTitle="$t('buttons.apply_range')"
                            :dateFormat="dateFormat"
                    >
                    </date-range-picker>
                </div>
            </div>
            <div class="chart-panel__chart">
                <apexchart height="100%" width="100%" type="scatter" :options="chartOptions" :series="series">
                </apexchart>
            </div>
        </div>
    </div>
</template>
<script>
    import VueApexCharts from 'vue-apexcharts';
    import {chartPeriods} from '@/config/charts';
    import {mapGetters} from 'vuex';
    import 'vue-mj-daterangepicker/dist/vue-mj-daterangepicker.css';

    export default {
        name: 'DetailChartPanel',
        components: {
            apexchart: VueApexCharts,
        },
        props: {
            unit: {
                type: Object,
                required: true,
            },
            loadedData: {
                type: Array,
                default() {
                    return [];
                },
            },
        },
        created() {
            this.showCustomDateRange = false;
            if (!this.loadedData.length === 0) {
                this.loading = false;
            }
        },
        data() {
            return {
                // START CHART OPTION
                loading: true,
                series: [
                    {
                        name: 'Events',
                        data: [],
                    },
                ],
                // END CHART OPTIONS
                showChartSettings: false,
                showChartPeriodSelect: false,
                showChartPeriodSelect2: false,
                selectedPeriod: {id: 4, value: 'day', name: this.$t('interval.day')},
            };
        },
        computed: {
            ...mapGetters('tentacle', ['timeFormat']),
            computedPeriodOption() {
                const option = this.selectedPeriod;
                option.name = this.$t(`interval.${option.value}`);
                return option;
            },
            getCriticalColors() {
                const range = this.maxValue - this.minValue;
                const coef = 0.125;
                const retVal = [];
                retVal.push('rgba(0,0,0,0)');
                return retVal;
            },
            chartOptions() {
                return {
                    chart: {
                        toolbar: {
                            show: true,
                        },
                    },
                    colors: [function({ value, seriesIndex, w }) {
                        switch (value) {
                            case 3:
                                return '#cd0300';
                            case 2:
                                return '#f98e00';
                            case 1:
                                return '#59bbff';
                            case 0:
                                return 'rgba(183,79,255,0)';
                        }
                    }],
                    stroke: {
                        curve: 'straight',
                    },
                    annotations: {
                        show: true,
                        yaxis: [{
                            y: 10,
                            strokeDashArray: 5,
                            borderColor: this.getCriticalColors[0],
                            label: {
                                borderColor: '#6fbdea',
                                style: {
                                    color: '#fff',
                                    background: '#6fbdea',
                                    fontSize: '1.5em',
                                },
                                text: '',
                            },
                        },
                            {
                                y: 10,
                                strokeDashArray: 5,
                                borderColor: this.getCriticalColors[1],
                                label: {
                                    borderColor: '#ff5a5a',
                                    style: {
                                        color: '#fff',
                                        background: '#ff5a5a',
                                        fontSize: '1.5em',
                                    },
                                    text: '',
                                },
                            }],
                    },
                    legend: {
                        show: true,
                        showForSingleSeries: true,
                        fontSize: '14px',
                        fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                        offsetY: -10,
                        labels: {
                            colors: '#acb4c5',
                        },
                        itemMargin: {
                            horizontal: 10,
                            vertical: 10,
                        },
                    },
                    dataLabels: {
                        enabled: false,
                        formatter: (val, opts) => `${val}`,
                        textAnchor: 'middle',
                        style: {
                            fontSize: '12px',
                            fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                        },
                    },
                    fill: {
                        gradient: {
                            shadeIntensity: 1,
                            opacityFrom: 0.7,
                            opacityTo: 0.9,
                            stops: [0, 90, 100],
                        },
                    },
                    grid: {
                        show: true,
                        borderColor: '#eee',
                        strokeDashArray: 5,
                        position: 'back',
                        xaxis: {
                            lines: {
                                show: true,
                            }
                        },
                        yaxis: {
                            lines: {
                                show: true,
                            },
                        },
                    },
                    noData: {
                        text: this.$t('detail.missing'),
                        align: 'center',
                        verticalAlign: 'middle',
                        offsetX: 0,
                        offsetY: 0,
                        style: {
                            color: undefined,
                            fontSize: '14px',
                            fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                        },
                    },
                    tooltip: {
                        enabled: true,
                        style: {
                            fontSize: '12px',
                            fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                        },
                        x: {
                            formatter: (val, opts) => {
                                const newDate = new Date(val);
                                const outDate = this.moment(newDate).format(this.timeFormat ? 'DD MMM YYYY, hh:mm a' : 'DD MMM YYYY, HH:mm');
                                return `${outDate}`;
                            },
                        },
                        y: {
                            title: {
                                formatter: (seriesName) => {
                                    return seriesName;
                                },
                            },
                            formatter: (val, opts) => {
                                let messages = {
                                    1: this.$t('eventChart.info'),
                                    2: this.$t('eventChart.warning'),
                                    3: this.$t('eventChart.error'),
                                };
                                return messages[val];
                            },
                        },
                        z: {
                            title: this.$t('eventChart.message'),
                            formatter: (val, opts) => {
                                return val;
                            },
                        },
                        marker: {
                            show: true,
                        },
                    },
                    yaxis: {
                        decimalsInFloat: 2,
                        min: 0,
                        max: 4,
                        forceNiceScale: true,
                        labels: {
                            show: true,
                            minWidth: 70,
                            maxWidth: 70,
                            style: {
                                color: '#acb4c5',
                                fontSize: '15px',
                                fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                            },
                            formatter: function (val, index) {
                                let levels = {1: 'info', 2: 'warning', 3: 'error'};
                                return levels[index];
                            }
                        }
                    },
                    xaxis: {
                        type: 'datetime',
                        labels: {
                            // format: 'dd MMM yyyy',
                            style: {
                                colors: '#acb4c5',
                                fontSize: '12px',
                                fontFamily: 'Rubik, Helvetica, Arial, sans-serif',
                            },
                        },
                    },
                };
            },
            chartPeriodOptions() {
                chartPeriods.forEach(item => item.name = this.$t(`interval.${item.value}`));
                return chartPeriods;
            },
            hasValues() {
                return this.series.length > 0 && this.series[0].data.length > 0;
            },
            minValue() {
                if (this.hasValues) {
                    return this.series[0].data.reduce((total, current) => {
                        if (current[1] < total) {
                            total = current[1];
                        }
                        return Number(total);
                    }, this.series[0].data[0][1]);
                }
                return NaN;
            },
            lastEvent() {
                const {data} = this.series[0];
                console.log(this.series);
                let type = this.hasValues ? data[data.length - 1][1] : 0;
                let typeMessage = this.$t('eventChart.info').toUpperCase();
                switch (type) {
                    case 3:
                        typeMessage = this.$t('eventChart.error').toUpperCase();
                        break;
                    case 2:
                        typeMessage = this.$t('eventChart.warning').toUpperCase();
                        break;
                    case 0:
                        typeMessage = '';
                        break;
                }
                return typeMessage + ': ' + data[data.length - 1][2];
            },
            lastEventMessage() {
                let messageObject = JSON.parse(this.unit.lastEvent.message);
                switch (this.unit.lastEvent['name']) {
                    case 'activeGatewayChange':
                        const previousActiveNetwork = messageObject['previous'].charAt(0).toUpperCase() + messageObject['previous'].slice(1);
                        const activeNewNetwork = messageObject['active'].charAt(0).toUpperCase() + messageObject['active'].slice(1);
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {previous: previousActiveNetwork, active: activeNewNetwork});
                    case 'fportSettingsChange':
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + messageObject['fport'] + 'Message');
                    case 'networkChangeRequest':
                        const previousChangeNetwork = messageObject['previous'].charAt(0).toUpperCase() + messageObject['previous'].slice(1);
                        const newChangeNetwork = messageObject['new'].charAt(0).toUpperCase() + messageObject['new'].slice(1);
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {previous: previousChangeNetwork, new: newChangeNetwork});
                    case 'networkGatewaysCreated':
                        const networks = messageObject['networks'].map(network => network.charAt(0).toUpperCase() + network.slice(1)).join(', ');
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {networks: networks});
                    case 'networkGatewayActive':
                        const activeNetwork = messageObject['network'].charAt(0).toUpperCase() + messageObject['network'].slice(1);
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {network: activeNetwork});
                    case 'networkGatewayRemoved':
                        const removedNetwork = messageObject['network'].charAt(0).toUpperCase() + messageObject['network'].slice(1);
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {network: removedNetwork});
                    case 'sequence':
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {actual: messageObject['actual'], previous: messageObject['previous'] + 1});
                    case 'settings':
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {frame: messageObject['frame'], omitted: messageObject['omitted']});
                    case 'threshold':
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message');
                    case 'attention':
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message');
                    case 'customer':
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {name: messageObject['customer']['name']});
                    case 'lqi':
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {lqi: messageObject['lqi'], badCount: messageObject['badCount']});
                    case 'networkSwitching':
                        const nsType = messageObject['type'].charAt(0).toUpperCase() + messageObject['type'].slice(1);
                        return this.$t('eventChart.' + messageObject['service'] + nsType + 'Message', {
                            type: messageObject['type'].toUpperCase(),
                            service: messageObject['service'],
                            network: messageObject['network'].charAt(0).toUpperCase() + messageObject['network'].slice(1),
                        });
                    case 'selfAwarenessChange':
                        const uppedTrigger = messageObject['trigger'][0].toUpperCase() + messageObject['trigger'].slice(1);
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message' + uppedTrigger, {lqi: messageObject['lqi'], number: messageObject['number'], profile: messageObject['profile'], state: messageObject['state']});
                    case 'ack':
                        let expectedAt = new Date(Number(messageObject['expectedAt']) * 1000);
                        expectedAt.setHours(expectedAt.getHours() + 2);
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message', {expectedAt: this.moment(expectedAt).format(this.timeFormat ? 'DD MMM YYYY, hh:mm a' : 'DD MMM YYYY, HH:mm')});
                    default:
                        return this.$t('eventChart.' + this.unit.lastEvent['name'] + 'Message');
                }
            },
            maxValue() {
                if (this.hasValues) {
                    return this.series[0].data.reduce((total, current) => {
                        if (current[1] > total) {
                            total = current[1];
                        }
                        return Number(total);
                    }, this.series[0].data[0][1]);
                }
                return NaN;
            },
        },
        watch: {
            loadedData(newVal, oldVal) {
                this.updateData(newVal);
            },
        },
        methods: {
            getIntervalOption(value) {
                return this.chartPeriodOptions.find(item => item.value === value);
            },
            updateData(newData) {
                if (newData.length > 0 && typeof newData !== 'string') {
                    this.loading = true;
                    const series = [];
                    newData.forEach((data) => {
                        if (data.id) {
                            let dataName = data.name.charAt(0).toLowerCase() + data.name.slice(1);
                            let name = this.$t(`eventChart.${dataName}`);
                            const sensor = {
                                name: name,
                                data: [],
                            };

                            sensor.data = data.values.reduce((total, current) => {
                                let message = '';
                                let value = current.value;
                                let messageObject = JSON.parse(current.message);
                                switch (current.name) {
                                    case 'networkSwitching':
                                        const nsType = messageObject.type.charAt(0).toUpperCase() + messageObject.type.slice(1);
                                        message = this.$t('eventChart.' + messageObject.service + nsType + 'Message', {
                                            type: messageObject.type.toUpperCase(),
                                            service: messageObject.service,
                                            network: messageObject.network.charAt(0).toUpperCase() + messageObject.network.slice(1),
                                        });
                                        break;
                                    case 'selfAwarenessChange':
                                        const uppedTrigger = messageObject.trigger.charAt(0).toUpperCase() + messageObject.trigger.slice(1);
                                        message = this.$t('eventChart.' + current.name + 'Message' + uppedTrigger, {lqi: messageObject.lqi, number: messageObject.number, profile: messageObject.profile, state: messageObject.state});
                                        break;
                                    case 'activeGatewayChange':
                                        const previousActiveNetwork = messageObject.previous.charAt(0).toUpperCase() + messageObject.previous.slice(1);
                                        const activeNewNetwork = messageObject.active.charAt(0).toUpperCase() + messageObject.active.slice(1);
                                        message = this.$t('eventChart.' + current.name + 'Message', {previous: previousActiveNetwork, active: activeNewNetwork});
                                        break;
                                    case 'fportSettingsChange':
                                        message = this.$t('eventChart.' + current.name + messageObject.fport + 'Message');
                                        break;
                                    case 'networkChangeRequest':
                                        const previousChangeNetwork = messageObject.previous.charAt(0).toUpperCase() + messageObject.previous.slice(1);
                                        const newChangeNetwork = messageObject.new.charAt(0).toUpperCase() + messageObject.new.slice(1);
                                        message = this.$t('eventChart.' + current.name + 'Message', {previous: previousChangeNetwork, new: newChangeNetwork});
                                        break;
                                    case 'networkGatewaysCreated':
                                        const networks = messageObject.networks.map(network => network.charAt(0).toUpperCase() + network.slice(1)).join(', ');
                                        message = this.$t('eventChart.' + current.name + 'Message', {networks: networks});
                                        break;
                                    case 'networkGatewayActive':
                                        const activeNetwork = messageObject.network.charAt(0).toUpperCase() + messageObject.network.slice(1);
                                        message = this.$t('eventChart.' + current.name + 'Message', {network: activeNetwork});
                                        break;
                                    case 'networkGatewayRemoved':
                                        const removedNetwork = messageObject.network.charAt(0).toUpperCase() + messageObject.network.slice(1);
                                        message = this.$t('eventChart.' + current.name + 'Message', {network: removedNetwork});
                                        break;
                                    case 'sequence':
                                        message = this.$t('eventChart.' + current.name + 'Message', {actual: messageObject.actual, previous: messageObject.previous + 1});
                                        break;
                                    case 'settings':
                                        message = this.$t('eventChart.' + current.name + 'Message');
                                        if (typeof messageObject.frame !== 'undefined') {
                                            let actualFrame, previousFrame = this.$t('eventChart.default');
                                            if (messageObject.frame.actual !== ''){
                                                actualFrame = this.unit.payload.payloadFormats[messageObject.frame.actual].name;
                                            }
                                            if (messageObject.frame.previous !== ''){
                                                if (messageObject.frame.previous === null){
                                                    previousFrame = this.unit.payload.payloadFormats[6].name;
                                                } else {
                                                    previousFrame = this.unit.payload.payloadFormats[messageObject.frame.previous].name;
                                                }
                                            }
                                            let frame = this.$t('eventChart.frameMessage', {actual: actualFrame, previous: previousFrame});
                                            message = message + '<br>' + frame;
                                        }
                                        if (typeof messageObject.omitted !== 'undefined') {
                                            let actualInterval, previousInterval = this.$t('eventChart.default');
                                            let minutes = 15;
                                            if (typeof messageObject.omitted.interval !== 'undefined') {
                                                minutes = messageObject.omitted.interval;
                                            }
                                            if (messageObject.omitted.actual !== ''){
                                                actualInterval = ((messageObject.omitted.actual + 1) * minutes);
                                            }
                                            if (messageObject.omitted.previous !== ''){
                                                previousInterval = ((messageObject.omitted.previous + 1) * minutes);
                                            }
                                            let interval = this.$t('eventChart.intervalMessage', {actual: actualInterval, previous: previousInterval});
                                            message = message + '<br>' + interval;
                                        }
                                        if (typeof messageObject.messages !== 'undefined') {
                                            let accumulation = this.$t('eventChart.accumulationMessage', {actual: messageObject.messages.actual, previous: messageObject.messages.previous});
                                            message = message + '<br>' + accumulation;
                                        }
                                        if (typeof messageObject.interval !== 'undefined') {
                                            let measurementInterval = this.$t('eventChart.spanMessage', {actual: messageObject.interval.actual, previous: messageObject.interval.previous});
                                            message = message + '<br>' + measurementInterval;
                                        }
                                        if (typeof messageObject.user !== 'undefined') {
                                            let period = this.$t('eventChart.changedBy', {name: messageObject.user.name});
                                            message = message + '<br>' + period;
                                        }
                                        break;
                                    case 'power':
                                        message = this.$t('eventChart.' + current.name + 'Message');
                                        if (typeof messageObject.transmitting !== 'undefined') {
                                            let translatedPowerActual = this.$t(`transmittingPower.${messageObject.transmitting.actual}`);
                                            let translatedPowerPrevious = this.$t(`transmittingPower.${messageObject.transmitting.previous}`);
                                            let transmitting = this.$t('eventChart.transmittingMessage', {actual: translatedPowerActual, previous: translatedPowerPrevious});
                                            message = message + '<br>' + transmitting;
                                        }
                                        if (typeof messageObject.retries !== 'undefined') {
                                            let retries = this.$t('eventChart.retriesMessage', {actual: messageObject.retries.actual, previous: messageObject.retries.previous});
                                            message = message + '<br>' + retries;
                                        }
                                        if (typeof messageObject.period !== 'undefined') {
                                            let translatedPeriodActual = this.$t(`downlinkPeriods.${messageObject.period.actual}`);
                                            let translatedPeriodPrevious = this.$t(`downlinkPeriods.${messageObject.period.previous}`);
                                            let period = this.$t('eventChart.periodMessage', {actual: translatedPeriodActual, previous: translatedPeriodPrevious});
                                            message = message + '<br>' + period;
                                        }
                                        if (typeof messageObject.network !== 'undefined') {
                                            let translatedNetworkActual = this.$t(`networkTypes.${messageObject.network.actual}`);
                                            let translatedNetworkPrevious = this.$t(`networkTypes.${messageObject.network.previous}`);
                                            let network = this.$t('eventChart.networkTypesMessage', {actual: translatedNetworkActual, previous: translatedNetworkPrevious});
                                            message = message + '<br>' + network;
                                        }
                                        if (typeof messageObject.sframes !== 'undefined') {
                                            let translatedSfFramesActual = this.$t(`sfFrames.${messageObject.sframes.actual}`);
                                            let translatedSfFramesPrevious = this.$t(`sfFrames.${messageObject.sframes.previous}`);
                                            let sFrames = this.$t('eventChart.sfFramesMessage', {actual: translatedSfFramesActual, previous: translatedSfFramesPrevious});
                                            message = message + '<br>' + sFrames;
                                        }
                                        if (typeof messageObject.user !== 'undefined') {
                                            let period = this.$t('eventChart.changedBy', {name: messageObject.user.name});
                                            message = message + '<br>' + period;
                                        }
                                        break;
                                    case 'calibration':
                                        message = this.$t('eventChart.' + current.name + 'Message');
                                        if (typeof messageObject.calibration !== 'undefined') {
                                            let calibration = this.$t('eventChart.baseCalibrationMessage', {calibration: messageObject.calibration});
                                            message = message + '<br>' + calibration;
                                        }
                                        if (typeof messageObject.properties !== 'undefined') {
                                            let properties = this.$t('eventChart.propertiesCalibrationMessage', {properties: messageObject.properties});
                                            message = message + '<br>' + properties;
                                        }
                                        if (typeof messageObject.user !== 'undefined') {
                                            let period = this.$t('eventChart.changedBy', {name: messageObject.user.name});
                                            message = message + '<br>' + period;
                                        }
                                        break;
                                    case 'threshold':
                                        message = this.$t('eventChart.' + current.name + 'Message');
                                        if (typeof messageObject.temp !== 'undefined') {
                                            let translatedSensor = this.$t(`sensors.temp`);
                                            message = message + '<br>' + this.$t('eventChart.criticalMessage', {sensor: translatedSensor, value: messageObject.temp.value, unit: messageObject.temp.unit, threshold: messageObject.temp.threshold});
                                        }
                                        if (typeof messageObject.Moist !== 'undefined') {
                                            let translatedSensor = this.$t(`sensors.Moist`);
                                            message = message + '<br>' + this.$t('eventChart.criticalMessage', {sensor: translatedSensor, value: messageObject.Moist.value, unit: messageObject.Moist.unit, threshold: messageObject.Moist.threshold});
                                        }
                                        if (typeof messageObject.battery !== 'undefined') {
                                            let translatedSensor = this.$t(`sensors.battery`);
                                            message = message + '<br>' + this.$t('eventChart.criticalMessage', {sensor: translatedSensor, value: messageObject.battery.value, unit: messageObject.battery.unit, threshold: messageObject.battery.threshold});
                                        }
                                        if (typeof messageObject.light !== 'undefined') {
                                            let translatedSensor = this.$t(`sensors.light`);
                                            message = message + '<br>' + this.$t('eventChart.criticalMessage', {sensor: translatedSensor, value: messageObject.light.value, unit: messageObject.light.unit, threshold: messageObject.light.threshold});
                                        }
                                        break;
                                    case 'attention':
                                        message = this.$t('eventChart.' + current.name + 'Message');
                                        if (typeof messageObject.temp !== 'undefined') {
                                            let translatedSensor = this.$t(`sensors.temp`);
                                            message = message + '<br>' + this.$t('eventChart.notificationMessage', {sensor: translatedSensor, value: messageObject.temp.value, unit: messageObject.temp.unit, threshold: messageObject.temp.threshold});
                                        }
                                        if (typeof messageObject.Moist !== 'undefined') {
                                            let translatedSensor = this.$t(`sensors.Moist`);
                                            message = message + '<br>' + this.$t('eventChart.notificationMessage', {sensor: translatedSensor, value: messageObject.Moist.value, unit: messageObject.Moist.unit, threshold: messageObject.Moist.threshold});
                                        }
                                        if (typeof messageObject.battery !== 'undefined') {
                                            let translatedSensor = this.$t(`sensors.battery`);
                                            message = message + '<br>' + this.$t('eventChart.notificationMessage', {sensor: translatedSensor, value: messageObject.battery.value, unit: messageObject.battery.unit, threshold: messageObject.battery.threshold});
                                        }
                                        if (typeof messageObject.light !== 'undefined') {
                                            let translatedSensor = this.$t(`sensors.light`);
                                            message = message + '<br>' + this.$t('eventChart.notificationMessage', {sensor: translatedSensor, value: messageObject.light.value, unit: messageObject.light.unit, threshold: messageObject.light.threshold});
                                        }
                                        break;
                                    case 'customer':
                                        if (typeof messageObject.customer !== 'undefined') {
                                            message = this.$t('eventChart.customerMessage', {name: messageObject.customer.name});
                                        }
                                        if (typeof messageObject.user !== 'undefined') {
                                            let period = this.$t('eventChart.changedBy', {name: messageObject.user.name});
                                            message = message + '<br>' + period;
                                        }
                                        break;
                                    case 'lqi':
                                        message = this.$t('eventChart.' + current.name + 'Message', {lqi: messageObject.lqi, badCount: messageObject.badCount});
                                        break;
                                    case 'ack':
                                        let expectedAt = new Date(Number(messageObject.expectedAt) * 1000);
                                        expectedAt.setHours(expectedAt.getHours() + 2);
                                        message = this.$t('eventChart.' + current.name + 'Message', {expectedAt: this.moment(expectedAt).format(this.timeFormat ? 'DD MMM YYYY, hh:mm a' : 'DD MMM YYYY, HH:mm')});
                                        break;
                                    default:
                                        message = this.$t('eventChart.' + current.name + 'Message');
                                        break;
                                }
                                const timeInMilliseconds = current.timestamp * 1000;
                                total.push([timeInMilliseconds, value, message]);
                                return total;
                            }, []);
                            series.push(sensor);
                        }
                    });
                    this.series = series;
                }
                this.loading = false;
            },
            calcDateFromToday(year, month, day) {
                let cDate = new Date().getDate() + day;
                let cMonth = new Date().getMonth() + 1 + month;
                let cYear = new Date().getFullYear() + year;

                if (!this.isJanuary() && cDate < 1) {
                    let daysTotal = new Date(cYear, --cMonth, 0).getDate();
                    cDate = daysTotal + cDate;
                }
                //checks if day goes into december and counts back days
                if (this.isJanuary() && cDate < 1) {
                    cDate = 31 + cDate;
                    cMonth = 12;
                    cYear = cYear - 1;
                }
                //checks if month goes back to previous year and counts from 12
                if (this.isPreviousYearMonth(month)) {
                    cMonth = 12 + cMonth;
                    cYear = cYear - 1;
                }
                return `${cYear}-${cMonth}-${cDate}`;
            },
            getDateFromToday() {
                return `${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`;
            },
            isJanuary() {
                return (new Date().getMonth() + 1) == 1;
            },
            isPreviousYearMonth(month) {
                return ((new Date().getMonth() + 1 + month) <= 0);
            },
            setEventData(fromDate, toDate) {
                this.loading = true;
                const filter = [{from: fromDate, to: toDate}];
                const data = {tentacle: this.unit.id, filter: filter};
                this.$store.dispatch('tentacle/setEvent', data).then((resolve) => {
                    let chartData = [];
                    if (resolve.data) {
                        chartData = resolve.data;
                    }
                    this.updateData(chartData);
                });
            },
            toggleChartSettings() {
                this.showChartSettings = !this.showChartSettings;
            },
            toggleChartPeriodSelect() {
                this.showChartPeriodSelect = !this.showChartPeriodSelect;
            },
            toggleChartPeriodSelect2() {
                this.showChartPeriodSelect2 = !this.showChartPeriodSelect2;
            },
            hideChartPeriodSelect2() {
                this.showChartPeriodSelect2 = false;
            },
            hideChartPeriodSelect() {
                this.showChartPeriodSelect = false;
            },
            setChartcustomPeriod(picker) {
                this.showCustomDateRange = false;
                var dateFrom = new Date(picker.from);
                var dateTo = new Date(picker.to);
                var dateFromString = this.moment(dateFrom).format('YYYY-MM-DD');
                var dateToString = this.moment(dateTo).format('YYYY-MM-DD');

                this.setEventData(dateFromString, dateToString);
            },
            setChartPeriod(item) {

                this.showChartPeriodSelect = !this.showChartPeriodSelect;
                if (item.value === 'year' && this.selectedPeriod.value !== 'year') {
                    this.setEventData(this.calcDateFromToday(-1, 0, 0), this.getDateFromToday());
                } else if (item.value === 'month' && this.selectedPeriod.value !== 'month') {
                    this.setEventData(this.calcDateFromToday(0, -1, 0), this.getDateFromToday());
                } else if (item.value === 'week' && this.selectedPeriod.value !== 'week') {
                    this.setEventData(this.calcDateFromToday(0, 0, -6), this.getDateFromToday());
                } else if (item.value === 'day' && this.selectedPeriod.value !== 'day') {
                    this.setEventData(this.calcDateFromToday(0, 0, -1), this.getDateFromToday());
                } else if (item.value === 'customRange') {
                    this.showCustomDateRange = true;
                }
                this.selectedPeriod = item;
            },
        },
    };
</script>

<style lang="scss" scoped>
    .chart-panel {
        position: relative;
        margin-bottom: 4rem;
        border-radius: 2rem;
        background-color: $white;
        box-shadow: 0 0 5px rgba(100, 116, 160, 0.1);
        @media only screen and (max-width: 1199px) {
            width: 90%;
            margin: auto;
            margin-top: 2em;
        }
        @media only screen and (min-width: 1200px) and (max-width: 1400px) {
            margin-top: 5rem;
            margin-left: 2%;
            width: 94%;
        }

        &__header {
            padding: 0 3rem;
            height: 6rem;
            border-bottom: 0.1rem solid $border-color;
            display: flex;
            align-items: center;
            font-weight: 600;
            font-size: 1.4rem;
            color: $grey;

            &__settings {
                position: relative;
                margin-left: auto;
                cursor: pointer;
                display: flex;
                align-items: center;

                svg {
                    width: 1.8rem;
                    height: 1.8rem;
                }

                .icon {
                    fill: $grey;
                    width: 1.8rem 1.8rem;
                }

                .graph-settings {
                    position: absolute;
                    z-index: 12;
                    top: 3rem;
                    right: -1rem;
                    padding: 1rem;
                    width: 20rem;
                    height: 10rem;
                    background-color: $white;
                    box-shadow: 0 0.5rem 1.5rem 0 rgba(37, 44, 97, 0.15),
                    0 0.2rem 0.4rem 0 rgba(93, 100, 148, 0.2);
                }
            }
        }

        &__info {
            padding: 0 3rem;
            display: flex;
            height: 8rem;
            align-items: stretch;

            &__min,
            &__actual,
            &__max {
                min-width: 18rem;
                display: flex;
                height: 100%;
                align-items: center;
                font-size: 2.5rem;
                font-weight: 500;
                color: $dark;

                .value {
                    color: $grey;
                }

                .description {
                    font-size: 1.1rem;
                    width: 4.6rem;
                    display: inline-flex;
                    align-items: center;
                    height: 3rem;
                    text-align: end;
                    margin-right: 2rem;
                    position: relative;

                    &:after {
                        content: '';
                        display: inline-block;
                        position: absolute;
                        right: -1rem;
                        width: 0.1rem;
                        height: 100%;
                        background-color: $very-light-grey;
                    }
                }
            }

            &__period {
                position: relative;
                display: flex;
                align-items: center;
                margin-left: auto;
                font-weight: 500;
                color: $grey;
                align-self: center;
                padding: 1.5rem;
                border-radius: 0.5rem;
                cursor: pointer;
                font-size: 1.2rem;
                transition: all 0.4s;

                svg {
                    width: 1.2rem;
                    height: 1.2rem;
                }

                &:hover {
                    background-color: $bg;
                }

                span {
                    margin-left: 1rem;
                    min-width: 7rem;
                    color: $dark;
                }
            }
        }

        &__chart {
            padding: 0 3rem;
            min-height: 28rem;
            width: 100%;
            height: 280px;
            @media only screen and (max-width: 991px) {
                padding: 0;
            }
        }

        &__footer {
            height: 6rem;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.2rem;
            font-weight: 500;
            color: $grey;

            span {
                width: 2rem;
                height: 0.6rem;
                border-radius: 0.2rem;
                background-color: $company-color;
                margin-right: 0.5rem;
            }
        }
    }

    .loading-wrapper {
        position: absolute;
        border-radius: 2rem;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
    }
</style>
<style>
    .apexcharts-tooltip-z-group {
        display: flex;
    }
    .date-range-picker {
        z-index: 999;
        background-color: #fff;
        right: 30px;
        position: relative;
    }

    .mj-daterange-picker {
        background-color: #fff;
    }

    .date-range-picker .mj-daterange-picker-controls .mj-daterange-picker-submit {
        display: inline-flex !important;
    }

    .chart-panel__info__period span {
        margin-right: 1rem;
    }
</style>
